/** @format */
import { RefObject, useEffect } from 'react';

/**
 * Traps focus within an element
 * @param ref The reference to the dom element that will have focus trapped within it
 * @param watchers An array of values. When all are thruthy the focus trap will be active
 */
export const useFocusTrap = (
    ref: RefObject<HTMLElement>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    watchers: any[] = []
): void => {
    function getFocusableElements(): NodeListOf<HTMLElement> | [] {
        return ref.current
            ? ref.current.querySelectorAll(
                  'button:not([role="tab"]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              )
            : [];
    }

    function handler(event: KeyboardEvent) {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const { key, shiftKey } = event;
        const focusableElements = getFocusableElements();
        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement =
            focusableElements[focusableElements.length - 1];

        // Bail if it isn't the tab key that is pressed
        if (key !== 'Tab') {
            return;
        }

        if (shiftKey) {
            if (document.activeElement === firstFocusableElement) {
                lastFocusableElement.focus();
                event.preventDefault();
            }
        } else {
            // eslint-disable-next-line no-lonely-if -- Legacy code: do not use as an example.
            if (document.activeElement === lastFocusableElement) {
                firstFocusableElement.focus();
                event.preventDefault();
            }
        }
    }

    useEffect(() => {
        const isModalOpen = watchers.every((val) => !!val);
        if (!isModalOpen) return;

        const previouslyFocusedElement = document.activeElement;
        const focusableElements = getFocusableElements();
        const firstFocusableElement = focusableElements[0];

        if (firstFocusableElement) {
            // focus the first element in the focus trap
            firstFocusableElement.focus();
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
        ref.current && ref.current.addEventListener('keydown', handler);

        return () => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain, react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
            ref.current && ref.current.removeEventListener('keydown', handler);
            if (
                previouslyFocusedElement !== null &&
                document.body.contains(previouslyFocusedElement) // check reference to node is still in document
            ) {
                // focus the element that was previously focused after closing the focus trap
                (previouslyFocusedElement as HTMLElement).focus();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, watchers);
};
